import { GeoJsonFeatureFactory, RotationUnit } from '../geojson/GeoJsonFeatureFactory';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import * as olExtent from 'ol/extent';
import LayerGroup from 'ol/layer/Group';
import VectorTile from 'ol/layer/VectorTile';
import { PMTilesVectorSource } from 'ol-pmtiles';
import Static from 'ol/source/ImageStatic';
import { transform as olTransform } from 'ol/proj';

import { IDrawing } from './drawing-types';
import { LoadingManager } from '@shared/models/loading-manager';
import XYZ from 'ol/source/XYZ';
import { DrawingLoaders } from './drawing-loaders';
import TileLayer from 'ol/layer/Tile';
import { Geometry } from 'ol/geom';
import ImageLayer from 'ol/layer/Image';
import geojsonvt from 'geojson-vt';
import { GeoJsonUtils } from '../geojson/OlGeojsonUtils';
import VectorTileLayer from 'ol/layer/VectorTile';
import VectorTileSource from 'ol/source/VectorTile.js';
import { TreeNode } from '@shared/ui-native/Tree';
import { DrawingUtils } from './drawing-constants';
import Feature from 'ol/Feature';
import { Auth } from '@shared/Auth';

// New: same origin, proxied through kuura — single uuid
const DRAWING_API_BASE = '/kuura/v1/viz/drawing/';

// Old: direct microservice — project + file uuid (extract projectUuid from status response)
//const DRAWING_API_BASE = `${kuura.AwsApiGatewayDomain()}/viz/v1/drawings/`;

const drawingUrl = (uuid: string, endpoint: string): string => `${DRAWING_API_BASE}${uuid}/${endpoint}`;

// Old microservice variant (uncomment with old DRAWING_API_BASE):
// const drawingUrl = (projectUuid: string, fileUuid: string, endpoint: string): string =>
//     `${DRAWING_API_BASE}${projectUuid}/${fileUuid}/${endpoint}`;

const fetchDrawingStatus = async (uuid: string): Promise<any> => {
    const headers = await Auth.getHeaders();
    const response = await fetch(drawingUrl(uuid, 'status'), {
        headers,
    });

    if (!response.ok) {
        throw new Error('Failed to fetch drawing status');
    }
    const json = await response.json();
    console.log(json);
    return json;
};

export const createDrawing = async (uuid: string, node?: TreeNode): Promise<IDrawing | null> => {
    const layerGroup = new LayerGroup({});
    const isMobile = window.innerWidth <= 800 || window.location.pathname.includes('mobile');

    const drawingExtent = olExtent.createEmpty();
    const config = isMobile ? DrawingUtils.MOBILE_CONFIG : DrawingUtils.DESKTOP_CONFIG;

    const status: any = await fetchDrawingStatus(uuid);
    const preGeneratedRasterTilesAvailable: boolean = status.rasterTilesAvailable;

    const rasterMinZoom = config.rasterMinZoom;
    const tileMinZoom = config.tileMinZoom;
    const rasterMaxZoom = isMobile && preGeneratedRasterTilesAvailable ? status.maxRasterZoom : config.rasterMaxZoom;

    const wgs84ToMapExtent = (minCoord: number[], maxCoord: number[]): olExtent.Extent => {
        const min = olTransform(minCoord, 'EPSG:4326', 'EPSG:3857');
        const max = olTransform(maxCoord, 'EPSG:4326', 'EPSG:3857');

        const transformedExtent: olExtent.Extent = olExtent.createEmpty();
        olExtent.extendCoordinates(transformedExtent, [min, max]);
        return transformedExtent;
    };

    const statusBoundingBoxExtent = (): olExtent.Extent | null => {
        const bbox = status.boundingBox;
        if (!bbox) {
            return null;
        }
        const { minLatitude, maxLatitude, minLongitude, maxLongitude } = bbox;
        if (minLatitude == null || maxLatitude == null || minLongitude == null || maxLongitude == null) {
            return null;
        }
        return wgs84ToMapExtent([minLongitude, minLatitude], [maxLongitude, maxLatitude]);
    };

    const fetchImageExtent = async (): Promise<olExtent.Extent | null> => {
        const headers = await Auth.getHeaders();
        const response = await fetch(drawingUrl(uuid, 'image/16'), {
            headers,
        });
        if (!response.ok) {
            return null;
        }

        const imageResponse = await response.json();
        if (!Array.isArray(imageResponse.extent) || imageResponse.extent.length !== 4) {
            return null;
        }
        return wgs84ToMapExtent([imageResponse.extent[0], imageResponse.extent[1]], [imageResponse.extent[2], imageResponse.extent[3]]);
    };

    /* Resolves the drawing extent: prefer the bounding box from the status response;
     ** if the status has no bounds (older migrated statuses), fall back to fetching
     ** the static image metadata for zoom 16 and using its extent. */
    const createExtent = async (): Promise<olExtent.Extent> => {
        try {
            const fromStatus = statusBoundingBoxExtent();
            if (fromStatus) {
                return fromStatus;
            }

            const fromImage = await fetchImageExtent();
            if (fromImage) {
                return fromImage;
            }
            return olExtent.createEmpty();
        } catch (e) {
            console.warn(e);
            return olExtent.createEmpty();
        }
    };

    const createVectorTileLayer = async (minZoom = 16, maxZoom = 24): Promise<VectorTileLayer> => {
        const headers = await Auth.getHeaders();
        const response = await fetch(drawingUrl(uuid, 'lines'), {
            headers,
        });
        const linesResponse = await response.json();

        // calculate extent from line features
        const lineFeatures: Array<Feature<Geometry>> = DrawingUtils.GEOJSON_FORMAT.readFeatures(linesResponse, { featureProjection: DrawingUtils.projection });
        lineFeatures.forEach(feature => {
            const featureExtent: olExtent.Extent | undefined = feature.getGeometry()?.getExtent();
            if (featureExtent) {
                olExtent.extend(drawingExtent, featureExtent);
            }
        });

        const tileIndex = geojsonvt(linesResponse, {
            minZoom: minZoom,
            maxZoom: Math.min(maxZoom, 24),
            tolerance: 5,
            extent: 4096,
        });

        const vectorTileLoader = (tile, src) => {
            const tileCoord = JSON.parse(src);

            const tileData = tileIndex.getTile(tileCoord[0], tileCoord[1], tileCoord[2]);

            const geojson: string = JSON.stringify(
                {
                    type: 'FeatureCollection',
                    features: tileData ? tileData.features : [],
                },
                GeoJsonUtils.replacer
            );

            const actualFeatures = DrawingUtils.VECTOR_TILE_FORMAT.readFeatures(geojson, {
                extent: DrawingUtils.tileGrid.getTileCoordExtent(tileCoord),
                featureProjection: 'EPSG:3857',
            });

            tile.setFeatures(actualFeatures);
        };

        const vectorSource = new VectorTileSource({
            maxZoom: 24,
            tileUrlFunction: tileCoord => JSON.stringify(tileCoord),
            tileLoadFunction: vectorTileLoader,
        });

        const vectorTileLayer = new VectorTileLayer({
            source: vectorSource,
            maxZoom: 24,
            minZoom: 6,
            style: GeoJsonFeatureFactory.STYLE.polyStyle,
        });

        return vectorTileLayer;
    };

    const createVectorLayer = async (pmtilesUrl: string, minZoom: number, maxZoom: number): Promise<VectorTile> => {
        const source = new PMTilesVectorSource({
            url: pmtilesUrl,
        });

        const pmTilesLayer = new VectorTile({
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            source: source,
            maxZoom: maxZoom,
            minZoom: minZoom,
            style: GeoJsonFeatureFactory.STYLE.polyStyle,
        });

        return pmTilesLayer;
    };

    const createRasterTileLayer = async (url: string, minZoom: number, maxZoom: number): Promise<TileLayer<XYZ>> => {
        const tileSource = new XYZ({
            url: url,
            tileLoadFunction: async (tile, src) => {
                await DrawingLoaders.imageTileLoader(tile, src);
            },
        });

        const rasterLayer = new TileLayer({
            source: tileSource,
            minZoom: minZoom,
            maxZoom: maxZoom,
            extent: drawingExtent,
        });

        return rasterLayer;
    };

    const createPolygonLayer = async (minZoom: number, maxZoom: number): Promise<VectorLayer<VectorSource> | null> => {
        try {
            const headers = await Auth.getHeaders();
            const response = await fetch(drawingUrl(uuid, 'polygons'), {
                headers,
            });
            const polygonFeatureResponse = await response.json();
            const polygonFeatures: Array<Feature<Geometry>> = DrawingUtils.GEOJSON_FORMAT.readFeatures(polygonFeatureResponse, { featureProjection: 'EPSG:3857' });
            polygonFeatures.forEach(feature => {
                const featureExtent: olExtent.Extent | undefined = feature.getGeometry()?.getExtent();
                if (featureExtent) {
                    olExtent.extend(drawingExtent, featureExtent);
                }
            });

            const polygonSource = new VectorSource({
                format: DrawingUtils.GEOJSON_FORMAT,
                features: polygonFeatures,
            });

            const polygonLayer = new VectorLayer({
                source: polygonSource,
                style: GeoJsonFeatureFactory.STYLE.polyStyle,
                minZoom: minZoom,
                maxZoom: maxZoom,
            });

            return polygonLayer;
        } catch (e) {
            console.error(e);
            return null;
        }
    };

    const createTextLayer = async (minZoom: number, maxZoom: number): Promise<VectorLayer<VectorSource<any>>[]> => {
        LoadingManager.startLoading(uuid + '_text');
        const returnToken: () => void = await DrawingUtils.rateLimiter.requestToken();
        const layers = [];
        try {
            const headers = await Auth.getHeaders();
            const response = await fetch(drawingUrl(uuid, 'text'), {
                headers,
            });
            const textFeatureResponse = await response.json();

            const textFeatures: Array<Feature<Geometry>> = DrawingUtils.GEOJSON_FORMAT.readFeatures(textFeatureResponse);
            textFeatures.forEach(feature => {
                if (minZoom === 10) {
                    feature.set('TEXT_ONLY', true);
                }

                const featureExtent: olExtent.Extent | undefined = feature.getGeometry()?.getExtent();
                if (featureExtent) {
                    olExtent.extend(drawingExtent, featureExtent);
                }
            });

            const rotations: number[] = textFeatures.map(feature => feature.get('ROTATION') || 0);
            const isRadians: boolean = rotations.every(rotation => rotation >= 0 && rotation <= DrawingUtils.MAX_RADIAN_VALUE);

            if (isRadians) {
                textFeatures.forEach(feature => feature.set('ROTATION_UNITS', RotationUnit.RADIANS));
            } else {
                textFeatures.forEach(feature => feature.set('ROTATION_UNITS', RotationUnit.DEGREES));
            }

            const bigTextFeatures = textFeatures.filter(text => text.get('HEIGHT') > 50);

            const vectorSource = new VectorSource({
                features: textFeatures.filter(text => !bigTextFeatures.includes(text)),
            });

            const textLayer = new VectorLayer({
                source: vectorSource,
                style: GeoJsonFeatureFactory.STYLE.textStyle,
                minZoom: minZoom,
                maxZoom: maxZoom,
            });

            if (bigTextFeatures.length > 0) {
                const bigTextVectorSource = new VectorSource({
                    features: bigTextFeatures,
                });

                const bigTextLayer = new VectorLayer({
                    source: bigTextVectorSource,
                    style: GeoJsonFeatureFactory.STYLE.textStyle,
                    minZoom: 1,
                    maxZoom: maxZoom,
                });

                layers.push(bigTextLayer);
            } else {
                layers.push(null);
            }

            layers.unshift(textLayer);
            return layers;
        } catch (e) {
            console.error(e);
            return [null, null];
        } finally {
            returnToken();
            LoadingManager.finishLoading(uuid + '_text');
        }
    };

    const createImageLayer = async (minZoom: number, maxZoom: number): Promise<ImageLayer<Static> | null> => {
        const returnToken: () => void = await DrawingUtils.rateLimiter.requestToken();

        try {
            const headers = await Auth.getHeaders();

            const response = await fetch(drawingUrl(uuid, 'image/16'), {
                headers,
            });

            const imageResponse = await response.json();
            const minCoord = [imageResponse.extent[0], imageResponse.extent[1]];
            const maxCoord = [imageResponse.extent[2], imageResponse.extent[3]];

            const min = olTransform(minCoord, 'EPSG:4326', 'EPSG:3857');
            const max = olTransform(maxCoord, 'EPSG:4326', 'EPSG:3857');

            const transformedExtent: olExtent.Extent = olExtent.createEmpty();
            olExtent.extendCoordinates(transformedExtent, [min, max]);
            olExtent.extend(drawingExtent, transformedExtent);

            const source = new Static({
                url: imageResponse.url,
                projection: DrawingUtils.projection,
                imageExtent: transformedExtent,
                crossOrigin: 'anonymous',
            });

            const imageLayer = new ImageLayer({
                source: source,
                minZoom: minZoom,
                maxZoom: maxZoom,
            });

            return imageLayer;
        } catch (e) {
            console.error(e);
            return null;
        } finally {
            returnToken();
        }
    };

    const refreshPmTiles = async () => {
        const pmTilesLayer = layerGroup.getLayersArray().find(layer => layer instanceof VectorTile && (layer as VectorTile).getSource() instanceof PMTilesVectorSource);

        if (pmTilesLayer) {
            layerGroup.getLayers().remove(pmTilesLayer);
            pmTilesLayer.dispose();
        }

        const headers = await Auth.getHeaders();
        const response = await fetch(drawingUrl(uuid, 'pmtiles-url'), {
            headers,
        });

        const updatedPMTilesResponse = await response.json();
        const newPmTilesLayer: VectorTile = await createVectorLayer(updatedPMTilesResponse.url, tileMinZoom, config.tileMaxZoom);
        layerGroup.getLayers().push(newPmTilesLayer);
    };

    const setupPmTilesAutoRefresh = () => {
        setInterval(async () => {
            await refreshPmTiles();
        }, 59 * 60 * 1000); // Refresh every 59 minutes
    };

    /*
     ** OVERVIEW:
     ** The load() function implements a multi-tier drawing visualization strategy
     ** that adapts to device capabilities and data availability. It loads drawing
     ** data in layers: raster base layer (either pre-generated tiles or static image),
     ** vector geometry (PMTiles or on-demand tiles), text labels, and polygon fills.
     ** Zoom level ranges are carefully managed to optimize performance on mobile
     ** devices while maintaining visual quality on desktop.
     **
     ** ARCHITECTURE:
     ** The function follows a waterfall loading pattern:
     **   1. Fetch drawing status and determine available data sources
     **   2. Load base raster layer (XYZ tiles or static image)
     **   3. Load vector geometry at appropriate zoom levels
     **   4. Load text and polygon overlays
     **
     ** MOBILE OPTIMIZATION:
     ** On mobile devices, vector tiles are restricted to zoom level 20+ to prevent
     ** browser crashes from excessive geometry. Pre-generated raster tiles provide
     ** visualization at lower zoom levels. This threshold (20) was determined
     ** empirically to balance visual quality against device memory constraints.
     **
     ** FALLBACK STRATEGY:
     ** When pre-generated data is unavailable, the system falls back to:
     **   - Static image instead of XYZ tiles
     **   - Client-side vector tiling (geojson-vt) instead of PMTiles
     ** This ensures drawings remain viewable even when batch processing is incomplete.
     */

    let pmtilesResponse: { url: string | null };

    if (status.pmtilesAvailable) {
        // If PMTiles are not available, no need to fetch the URL
        const headers = await Auth.getHeaders();
        const response = await fetch(drawingUrl(uuid, 'pmtiles-url'), {
            headers,
        });
        pmtilesResponse = await response.json();
    } else {
        pmtilesResponse = { url: null };
    }

    console.log('PMTILES RESPONSE:', pmtilesResponse);

    const vectorTilesAvailable: boolean = status.pmtilesAvailable && pmtilesResponse.url !== null;
    const detailedMinZoom = preGeneratedRasterTilesAvailable && isMobile ? status.maxRasterZoom : tileMinZoom;

    if (node && status.processingRequested && !status.processingCompleted) {
        //node.setSecondaryIcon('iki iki-process');
    }

    const logLines: string[] = [];
    const name = node ? node.text : uuid;
    logLines.push('##### Loading drawing', name, '#####');

    /* RASTER BASE LAYER:
     ** Attempt to load the foundational raster imagery. This provides
     ** a complete view of the drawing at lower zoom levels before
     ** vector geometry becomes viable.
     */
    let rasterLayerLoaded = false;
    if (preGeneratedRasterTilesAvailable) {
        /* The batch processing has pregenerated a XYZ tile layer for this drawing.
         ** Use it for efficient multi-resolution display. */

        logLines.push(`Loading XYZ tiles as base layer. [${rasterMinZoom} - ${rasterMaxZoom}]`);
        olExtent.extend(drawingExtent, await createExtent());
        const rasterTileLayer: TileLayer<XYZ> = await createRasterTileLayer(status.rasterTileUrl, rasterMinZoom, rasterMaxZoom);
        layerGroup.getLayers().push(rasterTileLayer);
        rasterLayerLoaded = true;
    } else {
        /* No pregenerated tiles found. Load one static image instead which
         ** covers the whole drawing. This is less efficient but ensures
         ** the drawing remains visible. */
        logLines.push(`Loading static image as base layer. [${rasterMinZoom} - ${rasterMaxZoom}]`);
        const imageLayer: ImageLayer<Static> | null = await createImageLayer(rasterMinZoom, rasterMaxZoom);

        if (imageLayer) {
            layerGroup.getLayers().push(imageLayer);
        }

        rasterLayerLoaded = imageLayer !== null;
    }

    /* VECTOR TILE ZOOM CALCULATION:
     ** Determine the minimum zoom level at which to display vector tiles.
     ** This critical decision balances visual quality against device
     ** performance constraints.
     */
    let vectorTileMinZoomLevel = detailedMinZoom;

    if (!rasterLayerLoaded) {
        /* If no raster base layer could be loaded, show vector tiles from
         ** zoom level 1 to ensure some visualization is available */
        vectorTileMinZoomLevel = 1;
    } else if (isMobile && !preGeneratedRasterTilesAvailable) {
        /* On mobile without pre-generated tiles, defer vector tiles to zoom 20
         ** to prevent memory exhaustion from rendering excessive geometry */
        vectorTileMinZoomLevel = 20;
    }

    const hasLines: boolean = status.pmtilesAvailable || status.rasterAvailable || status.rasterTilesAvailable;
    console.log('HAS LINES:', hasLines);

    /* POLYGON FILL LAYER:
     ** Load and push polygon fills before vector geometry so fills render below lines.
     ** LayerGroup renders in push order — polygon must be in the collection
     ** before the vector tile layer to avoid covering lines and text.
     */
    logLines.push(`Loading polygon layers. [${config.polygonMinZoom} - ${config.polygonMaxZoom}]`);
    const polygonMinZoom = hasLines ? config.polygonMinZoom : 10;
    const polygonLayer: VectorLayer<VectorSource> | null = await createPolygonLayer(polygonMinZoom, config.polygonMaxZoom);
    if (polygonLayer) {
        layerGroup.getLayers().push(polygonLayer);
    }

    /* VECTOR GEOMETRY LAYER:
     ** Load the primary geometric data using the most efficient available
     ** source. PMTiles are preferred for their compression and tiled access.
     */
    if (vectorTilesAvailable) {
        /* If we show vector tiles below zoom level 20 on mobile the amount of geometry
         ** loaded could still cause the browser to crash on mobile devices. The threshold 20
         ** isn't based on anything else than trying out different threshold until finding a
         ** good balance. */
        logLines.push(`Loading PMtiles. [${vectorTileMinZoomLevel} - ${27}]`);
        const pmTilesLayer: VectorTile = await createVectorLayer(pmtilesResponse.url, vectorTileMinZoomLevel, config.tileMaxZoom);
        layerGroup.getLayers().push(pmTilesLayer);

        // Setup auto-refresh of PMTiles layer every 59 minutes
        setupPmTilesAutoRefresh();
    } else {
        /* If no PMTiles are available, fall back to on-demand vector tiles. Load the geojson from server and perform
         ** vector tiling in the client using geojson-vt. This is computationally
         ** expensive but provides fallback functionality. */
        logLines.push(`Loading on-demand vector tiles. [${vectorTileMinZoomLevel} - ${27}]`);
        const vectorTileLayer: VectorTileLayer = await createVectorTileLayer(vectorTileMinZoomLevel, config.tileMaxZoom);
        layerGroup.getLayers().push(vectorTileLayer);
    }

    /* TEXT OVERLAY LAYER:
     ** Load text annotations last so they render above fills and lines.
     */
    const textMinZoom = hasLines ? config.textMinZoom : 10;
    logLines.push(`Loading text layers. [${textMinZoom} - ${config.textMaxZoom}]`);
    const [textLayer, bigTextLayer] = await createTextLayer(textMinZoom, config.textMaxZoom);

    if (textLayer) {
        layerGroup.getLayers().push(textLayer);
    }
    if (bigTextLayer) {
        layerGroup.getLayers().push(bigTextLayer);
    }

    logLines.push('############');
    console.log(logLines.join('\n'));

    const dispose = () => {
        layerGroup.getLayers().forEach(layer => {
            layer.dispose();
        });

        layerGroup.getLayers().clear();
    };

    const isExtentEmpty: boolean = !drawingExtent || olExtent.isEmpty(drawingExtent) || olExtent.getWidth(drawingExtent) === 0 || olExtent.getHeight(drawingExtent) === 0;

    if (isExtentEmpty && node) {
        node.setError('Extent is empty');
        node.setChecked(0);
        node.setEnabled(false);
        return null;
    }

    if (node) {
        const layerCounts = node.ajaxData.layerCounts;

        if (layerCounts.mapLayers > 0 && layerCounts.features > 0) {
            //node.setSecondaryIcon('iki iki-map-layers');
        } else {
            //node.setSecondaryIcon(null);
        }
    }

    return {
        uuid,
        layerGroup,
        extent: drawingExtent,
        dispose,
    };
};
